/** @jsxImportSource @emotion/react */
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { DataMap } from '@grapecity/wijmo.grid';
import { MdIvestMdlSpecComparison } from 'models/md/MdInvestResult';
import CustomGrid from 'components/grids/CustomGrid';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';

interface ModelSpecComparisonProps {
  data: MdIvestMdlSpecComparison[];
}

const ModelSpecComparisonTable: React.FC<ModelSpecComparisonProps> = ({ data }) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [code, setCode] = useState<any>();

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EVL_TP_CD', // 평가유형코드 (ex. 일치/불일치)
        optValCtn1: 'LIST',
      }),
    ]).then(([mdEvlTpCd]) => {
      setCode({
        mdEvlTpCd: mdEvlTpCd,
      });
    });
  };

  const gridData = useMemo(() => {
    return (data || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          ...cur,
          ctgrNm: cur.ctgrNm || cur.specNm,
        },
      ],
      [] as MdIvestMdlSpecComparison[]
    );
  }, [data]);

  const smlyMdlNm = useMemo(
    () => (data || []).filter((item) => item?.smlyMdlNm)[0]?.smlyMdlNm || '-',
    [data]
  );

  const layoutDefinition = useMemo(() => {
    const mapMdEvlTpCd = new DataMap(code?.mdEvlTpCd || [], 'cmnCd', 'cmnCdNm');
    return [
      {
        binding: 'ctgrNm',
        header: String(t('com.label.구분', '구분')),
        width: 82,
        minWidth: 50,
        align: 'center',
        allowMerging: true,
        cssClass: 'text-center transposed-header',
        cellTemplate: (params) => params.value || params.item.specNm || '',
      },
      {
        binding: 'specNm',
        header: String(t('com.label.구분', '구분')),
        width: 123,
        minWidth: 50,
        align: 'center',
        wordWrap: true,
        multiLine: true,
        allowMerging: true,
        cssClass: 'text-center transposed-header',
      },
      {
        binding: 'ivesMdlVal',
        header: String(t('md.label.검토 모델', '검토 모델')),
        width: '*',
        minWidth: 100,
        align: 'center',
        cellTemplate: (params) => params.value || '-',
      },
      {
        binding: 'smlyMdlVal',
        header: `${String(t('md.label.유사 모델', '유사 모델'))}${
          smlyMdlNm ? `(${smlyMdlNm})` : ''
        }`,
        width: '*',
        minWidth: 100,
        align: 'center',
        cellTemplate: (params) => params.value || '-',
      },
      {
        binding: 'difVal',
        header: String(t('md.label.차이', '차이')),
        width: 100,
        minWidth: 50,
        align: 'center',
        cellTemplate: (params) => {
          if (params.value && mapMdEvlTpCd.getDisplayValue(params.value)) {
            return mapMdEvlTpCd.getDisplayValue(params.value);
          }
          return params.value || '-';
        },
      },
    ];
  }, [code, smlyMdlNm]);

  /*
  // 그리드 컬럼 넓이를 자동조절하는 경우 주석제거 (현재는 넓이 고정인 상태에서 행높이 자동조절 적용)
  useEffect(() => {
    if (!gridRef.current || !(data || []).length) return;

    const col = gridRef.current?.columns.getColumn('specNm');
    if (!col) return;

    requestAnimationFrame(() => {
      gridRef.current?.autoSizeColumns(col.index, col.index);
    });
  }, [data]);
  */

  useEffect(() => {
    // 행 높이 자동조절
    gridRef.current?.autoSizeRows();
  }, [data]);

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.rows.defaultSize = 32;

    grid.formatItem.addHandler((s, e) => {
      const binding = s.columns[e.col].binding;
      if (e.panel === s.columnHeaders) {
        // 검토모델 헤더 컬럼의 tooltip 문구 변경
        if ('ivesMdlVal' === binding) {
          const item = s.collectionView?.items?.[0];
          if (item?.ivesMdlNm) {
            e.cell.dataset.tooltip = item?.ivesMdlNm;
          } else {
            delete e.cell.dataset.tooltip;
          }
        }
      }
    });
  });

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.모델 주요 사양 비교', '모델 주요 사양 비교')}</h3>
          {(data || []).length > 0 && <div className="info primary">{data?.[0]?.ivesMdlNm}</div>}
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={gridData}
        height={0}
        isSelector={false}
        isFilter={false}
        allowPinning={false}
        allowSorting={false}
        isReadOnly={true}
        isHoverCss={false}
        initialized={onInitialized}
      />
    </>
  );
};

export default ModelSpecComparisonTable;
